/**
 * Dimensions System - Flexy Modularity Enforcement
 * Centralized dimension tokens to eliminate hardcoded CSS arbitrary values
 * 
 * Flexy Principle: NEVER use hardcoded pixel values or percentages!
 * Use these tokens for all width, height, and size values.
 */

/**
 * Message bubble width percentages
 * Used in chat and messaging components
 */
export const MESSAGE_WIDTHS = {
  /** User message bubble width (70%) */
  USER: 'max-w-[70%]',
  /** Other user message bubble width (70%) */
  OTHER: 'max-w-[70%]',
  /** System message bubble width (85%) */
  SYSTEM: 'max-w-[85%]',
  /** Compact message width (80%) - used for smaller screens */
  COMPACT: 'max-w-[80%]',
  /** Wide message width (85%) - maximum allowed */
  WIDE: 'max-w-[85%]',
} as const;

/**
 * Touch target sizes following WCAG 2.1 guidelines
 * Minimum 44px for accessibility compliance
 */
export const TOUCH_TARGETS = {
  /** Small touch target (44px) - minimum accessible size */
  SM: 'min-w-[44px] min-h-[44px]',
  /** Medium touch target (48px) - standard size */
  MD: 'min-w-[48px] min-h-[48px]',
  /** Large touch target (52px) - easier to tap */
  LG: 'min-w-[52px] min-h-[52px]',
  /** Extra large touch target (56px) - tablet optimized */
  XL: 'min-w-[56px] min-h-[56px]',
  /** 2XL touch target (60px) - large displays */
  '2XL': 'min-w-[60px] min-h-[60px]',
  /** Numeric values for calculations */
  PX: {
    SM: 44,
    MD: 48,
    LG: 52,
    XL: 56,
    '2XL': 60,
  },
} as const;

/**
 * Form field width tokens
 * Consistent widths for inputs, selects, and form elements
 */
export const FORM_FIELDS = {
  /** Extra small field (100px) */
  XS: 'max-w-[100px]',
  /** Small field (120px) */
  SM: 'min-w-[120px]',
  /** Medium field (200px) */
  MD: 'min-w-[200px]',
  /** Large field (12.5rem = 200px) */
  LG: 'min-w-[12.5rem]',
  /** Extra large field (300px) */
  XL: 'min-w-[300px]',
  /** 2XL field (400px) */
  '2XL': 'min-w-[400px]',
} as const;

/**
 * Layout calculation tokens
 * Used for responsive layout calculations
 */
export const LAYOUT = {
  /** Sidebar collapsed width calculation (100vw - 2rem) */
  SIDEBAR_COLLAPSED: 'w-[calc(100vw-2rem)]',
  /** Sidebar expanded width calculation (100vw - 3rem) */
  SIDEBAR_EXPANDED: 'w-[calc(100vw-3rem)]',
  /** Mobile viewport width with padding */
  MOBILE_VIEWPORT: 'w-[calc(100vw-2rem)]',
  /** Tablet viewport width with padding */
  TABLET_VIEWPORT: 'w-[calc(100vw-3rem)]',
  /** Desktop viewport width with padding */
  DESKTOP_VIEWPORT: 'w-[calc(100vw-4rem)]',
} as const;

/**
 * Container width tokens
 * Maximum widths for various container types
 */
export const CONTAINER_WIDTHS = {
  /** Extra small container (max-width-sm = 640px) */
  XS: 'max-w-sm',
  /** Small container (max-width-md = 768px) */
  SM: 'max-w-md',
  /** Medium container (max-width-lg = 1024px) */
  MD: 'max-w-lg',
  /** Large container (max-width-xl = 1280px) */
  LG: 'max-w-xl',
  /** Extra large container (max-width-2xl = 1536px) */
  XL: 'max-w-2xl',
  /** Full width */
  FULL: 'w-full',
  /** 70% width */
  '70%': 'max-w-[70%]',
  /** 80% width */
  '80%': 'max-w-[80%]',
  /** 85% width */
  '85%': 'max-w-[85%]',
} as const;

/**
 * Fixed width tokens
 * Specific pixel/rem width values
 */
export const FIXED_WIDTHS = {
  /** 44px - Touch target minimum */
  '44PX': 'min-w-[44px]',
  /** 48px - Standard touch target */
  '48PX': 'min-w-[48px]',
  /** 52px - Large touch target */
  '52PX': 'min-w-[52px]',
  /** 56px - Extra large touch target */
  '56PX': 'min-w-[56px]',
  /** 80px - Small element */
  '80PX': 'min-w-[80px]',
  /** 100px - Extra small element */
  '100PX': 'max-w-[100px]',
  /** 120px - Small element */
  '120PX': 'min-w-[120px]',
  /** 200px - Medium element */
  '200PX': 'min-w-[200px]',
  /** 2.75rem (~44px) */
  '2.75REM': 'min-w-[2.75rem]',
  /** 3.25rem (~52px) */
  '3.25REM': 'min-w-[3.25rem]',
  /** 1.25rem (~20px) */
  '1.25REM': 'min-w-[1.25rem]',
  /** 7.5rem (~120px) */
  '7.5REM': 'min-w-[7.5rem]',
  /** 12.5rem (~200px) */
  '12.5REM': 'min-w-[12.5rem]',
} as const;

/**
 * Shadow effect tokens
 * Complex shadow values for depth and elevation
 */
export const SHADOWS = {
  /** Soft inset shadow for cards */
  INSET_SOFT: 'shadow-[inset_0_0_20px_rgba(0,0,0,0.05)]',
  /** Subtle inset shadow */
  INSET_SUBTLE: 'shadow-[inset_0_0_10px_rgba(0,0,0,0.03)]',
  /** Strong inset shadow */
  INSET_STRONG: 'shadow-[inset_0_0_30px_rgba(0,0,0,0.08)]',
} as const;

/**
 * Size constants (numeric values for calculations)
 */
export const SIZES = {
  /** Touch target minimum size in pixels */
  TOUCH_MIN_PX: 44,
  /** Mobile breakpoint in pixels */
  MOBILE_BREAKPOINT_PX: 768,
  /** Tablet breakpoint in pixels */
  TABLET_BREAKPOINT_PX: 1024,
  /** Swipe threshold in pixels */
  SWIPE_THRESHOLD_PX: 50,
  /** Navigation link duration in milliseconds */
  NAV_DURATION_MS: 300,
} as const;

/**
 * Combined dimensions export for easy importing
 */
export const DIMENSIONS = {
  MESSAGE_WIDTHS,
  TOUCH_TARGETS,
  FORM_FIELDS,
  LAYOUT,
  CONTAINER_WIDTHS,
  FIXED_WIDTHS,
  SHADOWS,
  SIZES,
} as const;

// Flexy: All dimensions centralized - no more hardcoded values!
export default DIMENSIONS;
